import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import type { OutcomeOption } from '@/api/firstConsultations/firstConsultationsApi.types';
import { useReplaceConsultationOutcomeOptionsMutation } from '@/hooks/useConsultationConfig';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Icon } from '@/components/common/Icon';
import { FieldError } from '@/components/ui/fieldError';
import { isBlank } from '@/utils/formValidation';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';

interface EditableOutcomeOption {
  code: string | null;
  labelEn: string;
  labelPl: string;
}

function toRows(options: OutcomeOption[]): EditableOutcomeOption[] {
  return options.map(function toRow(option) {
    return { code: option.code, labelEn: option.labelEn, labelPl: option.labelPl };
  });
}

interface OutcomeOptionsEditorProps {
  initialOptions: OutcomeOption[];
}

export function OutcomeOptionsEditor({ initialOptions }: OutcomeOptionsEditorProps) {
  const { t } = useTranslation('consultations');
  const mutation = useReplaceConsultationOutcomeOptionsMutation();
  const [rows, setRows] = useState<EditableOutcomeOption[]>(toRows(initialOptions));
  const [isSaving, setIsSaving] = useState(false);
  const [invalidRowIndexes, setInvalidRowIndexes] = useState<number[]>([]);

  useEffect(
    function syncFromServer() {
      setRows(toRows(initialOptions));
      setInvalidRowIndexes([]);
    },
    [initialOptions]
  );

  function updateRow(index: number, patch: Partial<EditableOutcomeOption>) {
    setInvalidRowIndexes([]);
    setRows(function apply(previous) {
      return previous.map(function mapRow(row, rowIndex) {
        return rowIndex === index ? { ...row, ...patch } : row;
      });
    });
  }

  function removeRow(index: number) {
    setRows(function apply(previous) {
      return previous.filter(function keep(_row, rowIndex) {
        return rowIndex !== index;
      });
    });
  }

  function addRow() {
    setRows(function apply(previous) {
      return [...previous, { code: null, labelEn: '', labelPl: '' }];
    });
  }

  async function save() {
    const incompleteIndexes = rows.reduce<number[]>(function collect(indexes, row, rowIndex) {
      if (isBlank(row.labelEn) || isBlank(row.labelPl)) {
        indexes.push(rowIndex);
      }
      return indexes;
    }, []);
    if (incompleteIndexes.length > 0) {
      setInvalidRowIndexes(incompleteIndexes);
      showErrorNotification(t('settings.incompleteRow'));
      return;
    }

    const drafts = rows.map(function toDraft(row) {
      return { code: row.code, labelEn: row.labelEn.trim(), labelPl: row.labelPl.trim() };
    });
    setIsSaving(true);
    try {
      const saved = await mutation.mutateAsync(drafts);
      setRows(toRows(saved));
      setInvalidRowIndexes([]);
      showSuccessNotification(t('settings.saved'));
    } catch (error) {
      const message = resolveApiErrorMessage(error);
      showErrorNotification(message.length > 0 ? message : t('settings.saveError'));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Card padding="md">
      <h2 className="text-sm font-bold text-zooindigo mb-4">{t('settings.outcomeOptionsTitle')}</h2>

      <div className="hidden sm:grid grid-cols-[1fr_1fr_auto] gap-2 mb-1.5 px-1">
        <span className="text-[10px] font-bold uppercase tracking-wider text-zoomuted">
          {t('settings.labelEn')}
        </span>
        <span className="text-[10px] font-bold uppercase tracking-wider text-zoomuted">
          {t('settings.labelPl')}
        </span>
        <span className="w-8" />
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-zoomuted mb-3">{t('settings.empty')}</p>
      ) : (
        <div className="space-y-2.5 mb-3">
          {rows.map(function renderRow(row, index) {
            return (
              <div
                key={index}
                className="flex flex-col sm:grid sm:grid-cols-[1fr_1fr_auto] gap-2.5 sm:gap-2 sm:items-center border-b border-zooborder pb-3 last:border-b-0 last:pb-0 sm:border-b-0 sm:pb-0"
              >
                <Input
                  value={row.labelEn}
                  hasError={invalidRowIndexes.includes(index) && isBlank(row.labelEn)}
                  onChange={function update(changeEvent) {
                    updateRow(index, { labelEn: changeEvent.target.value });
                  }}
                />
                <Input
                  value={row.labelPl}
                  hasError={invalidRowIndexes.includes(index) && isBlank(row.labelPl)}
                  onChange={function update(changeEvent) {
                    updateRow(index, { labelPl: changeEvent.target.value });
                  }}
                />
                <Button
                  variant="destructiveGhost"
                  size="icon"
                  aria-label={t('settings.removeRow')}
                  onClick={function remove() {
                    removeRow(index);
                  }}
                  className="self-start sm:self-auto w-8 h-8"
                >
                  <Icon name="Trash" />
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {invalidRowIndexes.length > 0 ? (
        <FieldError messageKey="consultations:settings.incompleteRow" />
      ) : null}

      <div className="flex items-center justify-between mt-3">
        <Button type="button" variant="outline" size="small" disabled={isSaving} onClick={addRow}>
          <Icon name="Plus" /> {t('settings.addRow')}
        </Button>
        <Button type="button" isLoading={isSaving} onClick={save}>
          {t('settings.save')}
        </Button>
      </div>
    </Card>
  );
}
